import { isEqual } from 'lodash-es';

import { deepMergeConfig } from '$lib/merge-config';

import {
  EmptyAgent,
  EmptyBilling,
  EmptyMaintenance,
  EmptyNetwork,
  type FragmentID,
  isCommonResource,
  isComponent,
  isJob,
  isLocalModule,
  isPackage,
  isRegistryModule,
  isRemote,
  isService,
  isTrigger,
  type PartID,
  type ReadonlyAgent,
  type ReadonlyBilling,
  type ReadonlyCommonResource,
  type ReadonlyComponent,
  type ReadonlyFragment,
  type ReadonlyFragmentImport,
  type ReadonlyJob,
  type ReadonlyLocalModule,
  type ReadonlyMaintenance,
  type ReadonlyNetwork,
  type ReadonlyPackage,
  type ReadonlyRegistryModule,
  type ReadonlyRemote,
  type ReadonlyResource,
  type ReadonlyResourceParent,
  type ReadonlyService,
  type ReadonlySingletonResource,
  type ReadonlyTrigger,
  SingletonResourceTypes,
} from './config-item';
import type { ConfigState } from './config-store';
import {
  isInvalidMachineConfigPart,
  isMachineConfigPart,
  type ReadonlyMachineConfig,
  type ReadonlyMachineConfigPart,
  type ReadonlyMaybeMachineConfigPart,
} from './machine-config';

export interface PartIDAndName {
  id: PartID;
  name: string;
}

export const getPart = (
  config: ConfigState,
  partID: PartID
): ReadonlyMachineConfigPart | undefined => {
  const part = config.current.parts[partID];

  if (!part || isInvalidMachineConfigPart(part)) {
    return undefined;
  }

  return part;
};

export const getAllPartIDs = (config: ConfigState): readonly PartID[] => {
  const { mainID, subIDs } = config.current;
  return mainID ? [mainID, ...subIDs] : [];
};

export const getPartIDsAndNames = (
  config: ConfigState
): readonly PartIDAndName[] => {
  const { mainID, subIDs } = config.current;
  const mainPart = getMaybePart(config, mainID);
  const subParts = subIDs.flatMap((id) => {
    const subPart = getMaybePart(config, id);
    return subPart ? { id: subPart.id, name: subPart.name } : [];
  });

  return mainPart
    ? [{ id: mainPart.id, name: mainPart.name }, ...subParts]
    : [];
};

export const getMaybePart = (
  config: ConfigState,
  partID: PartID | undefined
): ReadonlyMaybeMachineConfigPart | undefined => {
  return partID === undefined ? undefined : config.current.parts[partID];
};

export const getFragmentImports = (
  resourceParent: ReadonlyResourceParent | undefined
): readonly ReadonlyFragmentImport[] => {
  return Object.values(resourceParent?.fragmentImports ?? {});
};

export const getFragmentImport = (
  resourceParent: ReadonlyResourceParent | undefined,
  fragmentID: FragmentID,
  prefix?: string
): ReadonlyFragmentImport | undefined => {
  return getFragmentImports(resourceParent).find(
    (fragmentImport) =>
      fragmentImport.fragmentID === fragmentID &&
      fragmentImport.prefix === prefix
  );
};

export const getFragmentImportsFromParts = (
  config: ReadonlyMachineConfig
): ReadonlyFragmentImport[] => {
  return Object.values(config.parts)
    .filter(isMachineConfigPart)
    .flatMap(getFragmentImports);
};

/**
 * Returns the resolved child fragments contributed by enabled imports only. A
 * disabled fragment import provides no resources or singletons to the effective
 * config, though the import itself stays listed and its detail view still
 * renders. Applied here so both the machine and fragment builders exclude
 * disabled fragments wherever resources are expanded.
 */
const getEnabledChildFragments = (
  parent: ReadonlyResourceParent,
  childFragments: readonly ReadonlyFragment[]
): readonly ReadonlyFragment[] => {
  const disabledKeys = new Set(
    getFragmentImports(parent)
      .filter((fragmentImport) => fragmentImport.isDisabled)
      .map(
        (fragmentImport) =>
          `${fragmentImport.fragmentID}/${fragmentImport.prefix ?? ''}`
      )
  );
  if (disabledKeys.size === 0) {
    return childFragments;
  }
  return childFragments.filter(
    (fragment) => !disabledKeys.has(`${fragment.id}/${fragment.prefix ?? ''}`)
  );
};

export const getResourceParentResources = (
  parent: ReadonlyResourceParent | undefined,
  childFragments: readonly ReadonlyFragment[]
): readonly ReadonlyResource[] => {
  if (!parent) {
    return [];
  }

  const fragments = getEnabledChildFragments(parent, childFragments);

  return [
    ...Object.values(parent.components),
    ...fragments.flatMap((frag) =>
      Object.values(frag.effectiveConfig.components)
    ),
    ...Object.values(parent.services),
    ...fragments.flatMap((frag) =>
      Object.values(frag.effectiveConfig.services)
    ),
    ...Object.values(parent.registryModules),
    ...fragments.flatMap((frag) =>
      Object.values(frag.effectiveConfig.registryModules)
    ),
    ...Object.values(parent.localModules),
    ...fragments.flatMap((frag) =>
      Object.values(frag.effectiveConfig.localModules)
    ),
    ...Object.values(parent.remotes),
    ...fragments.flatMap((frag) => Object.values(frag.effectiveConfig.remotes)),
    ...Object.values(parent.processes),
    ...fragments.flatMap((frag) =>
      Object.values(frag.effectiveConfig.processes)
    ),
    ...Object.values(parent.packages),
    ...fragments.flatMap((frag) =>
      Object.values(frag.effectiveConfig.packages)
    ),
    ...Object.values(parent.triggers),
    ...fragments.flatMap((frag) =>
      Object.values(frag.effectiveConfig.triggers)
    ),
    ...Object.values(parent.jobs),
    ...fragments.flatMap((frag) => Object.values(frag.effectiveConfig.jobs)),
    ...Object.values(parent.fragmentImports),
  ];
};

/**
 * Resources contributed by child fragments only: enabled imports when parent is available, every
 * given fragment when the parent config failed parsing and enablement is unknown.
 */
export const getChildFragmentResources = (
  parent: ReadonlyResourceParent | undefined,
  childFragments: readonly ReadonlyFragment[]
): readonly ReadonlyResource[] => {
  const fragments = parent
    ? getEnabledChildFragments(parent, childFragments)
    : childFragments;
  return fragments.flatMap((frag) => [
    ...Object.values(frag.effectiveConfig.components),
    ...Object.values(frag.effectiveConfig.services),
    ...Object.values(frag.effectiveConfig.registryModules),
    ...Object.values(frag.effectiveConfig.localModules),
    ...Object.values(frag.effectiveConfig.remotes),
    ...Object.values(frag.effectiveConfig.processes),
    ...Object.values(frag.effectiveConfig.packages),
    ...Object.values(frag.effectiveConfig.triggers),
    ...Object.values(frag.effectiveConfig.jobs),
  ]);
};

export const getPackages = (
  resources: readonly ReadonlyResource[]
): readonly ReadonlyPackage[] => {
  return resources.filter(isPackage);
};

export const getJobs = (
  resources: readonly ReadonlyResource[]
): readonly ReadonlyJob[] => {
  return resources.filter(isJob);
};

export const getTriggers = (
  resources: readonly ReadonlyResource[]
): readonly ReadonlyTrigger[] => {
  return resources.filter(isTrigger);
};

export const getRemotes = (
  resources: readonly ReadonlyResource[]
): readonly ReadonlyRemote[] => {
  return resources.filter(isRemote);
};

export const getServices = (
  resources: readonly ReadonlyResource[]
): readonly ReadonlyService[] => {
  return resources.filter(isService);
};

export const getComponents = (
  resources: readonly ReadonlyResource[]
): readonly ReadonlyComponent[] => {
  return resources.filter(isComponent);
};

export const getCommonResources = (
  resources: readonly ReadonlyResource[]
): readonly ReadonlyCommonResource[] => {
  return resources.filter(isCommonResource);
};

export const getRegistryModules = (
  resources: readonly ReadonlyResource[]
): readonly ReadonlyRegistryModule[] => {
  return resources.filter(isRegistryModule);
};

export const getLocalModules = (
  resources: readonly ReadonlyResource[]
): readonly ReadonlyLocalModule[] => {
  return resources.filter(isLocalModule);
};

/**
 * De-duplicate modules that resolve to identical configuration.
 *
 * The same fragment can be imported more than once (for example with different
 * prefixes), which yields multiple module resources that share every field
 * except their per-import `id`. The backend collapses these references, so the
 * sidebar should present them as a single entry. Modules whose other fields
 * differ — e.g. a `version` driven by a fragment variable — are intentionally
 * kept as distinct entries so the divergence stays visible.
 */
export const dedupeModulesByContent = <
  T extends ReadonlyRegistryModule | ReadonlyLocalModule,
>(
  modules: readonly T[]
): readonly T[] => {
  const unique: T[] = [];
  for (const mod of modules) {
    const isDuplicate = unique.some((existing) =>
      isEqual({ ...existing, id: '' }, { ...mod, id: '' })
    );
    if (!isDuplicate) {
      unique.push(mod);
    }
  }
  return unique;
};

export const getSingletonResources = (
  resourceParent: ReadonlyResourceParent | undefined,
  fragments: readonly ReadonlyFragment[]
): readonly ReadonlySingletonResource[] => {
  return Object.values(SingletonResourceTypes)
    .map((type) => getSingletonResource(resourceParent, fragments, type))
    .filter((resource) => resource !== undefined);
};

export const getSingletonResource = <T extends ReadonlySingletonResource>(
  resourceParent: ReadonlyResourceParent | undefined,
  fragments: readonly ReadonlyFragment[],
  resourceType: T['type']
): T | undefined => {
  const enabledFragments = resourceParent
    ? getEnabledChildFragments(resourceParent, fragments)
    : fragments;
  switch (resourceType) {
    case SingletonResourceTypes.AGENT: {
      return getAgent(resourceParent, enabledFragments) as T;
    }
    case SingletonResourceTypes.NETWORK: {
      return getNetwork(resourceParent, enabledFragments) as T;
    }
    case SingletonResourceTypes.MAINTENANCE: {
      return getMaintenance(resourceParent, enabledFragments) as T;
    }
    case SingletonResourceTypes.BILLING: {
      return getBilling(resourceParent, enabledFragments) as T;
    }
    default: {
      return undefined;
    }
  }
};

const getBilling = (
  resourceParent: ReadonlyResourceParent | undefined,
  fragments: readonly ReadonlyFragment[]
): ReadonlyBilling | undefined => {
  const fragmentBillings = fragments.map(
    (fragment) => fragment.effectiveConfig.billing
  );
  if (
    fragmentBillings.every((billing) => billing === undefined) &&
    resourceParent?.billing === undefined
  ) {
    return undefined;
  }
  return deepMergeConfig<ReadonlyBilling>(
    [...fragmentBillings, resourceParent?.billing],
    EmptyBilling
  );
};

export const getNetwork = (
  resourceParent: ReadonlyResourceParent | undefined,
  fragments: readonly ReadonlyFragment[]
): ReadonlyNetwork | undefined => {
  const fragmentNetworks = fragments.map(
    (fragment) => fragment.effectiveConfig.network
  );
  if (
    fragmentNetworks.every((network) => network === undefined) &&
    resourceParent?.network === undefined
  ) {
    return undefined;
  }
  return deepMergeConfig<ReadonlyNetwork>(
    [...fragmentNetworks, resourceParent?.network],
    EmptyNetwork
  );
};

export const getAgent = (
  resourceParent: ReadonlyResourceParent | undefined,
  fragments: readonly ReadonlyFragment[]
): ReadonlyAgent | undefined => {
  const fragmentAgents = fragments.map(
    (fragment) => fragment.effectiveConfig.agent
  );

  if (
    fragmentAgents.every((agent) => agent === undefined) &&
    resourceParent?.agent === undefined
  ) {
    return undefined;
  }
  return deepMergeConfig<ReadonlyAgent>(
    [...fragmentAgents, resourceParent?.agent],
    EmptyAgent
  );
};

export const getMaintenance = (
  resourceParent: ReadonlyResourceParent | undefined,
  fragments: readonly ReadonlyFragment[]
): ReadonlyMaintenance | undefined => {
  const fragmentMaintenance = fragments.map(
    (fragment) => fragment.effectiveConfig.maintenance
  );
  if (
    fragmentMaintenance.every((maintenance) => maintenance === undefined) &&
    resourceParent?.maintenance === undefined
  ) {
    return undefined;
  }
  return deepMergeConfig<ReadonlyMaintenance>(
    [...fragmentMaintenance, resourceParent?.maintenance],
    EmptyMaintenance
  );
};
